/**
 * OrgSwitcher — dropdown que aparece en AppShell cuando el person
 * pertenece a 2+ organizations.
 *
 * Al cambiar la org:
 *   1. POST /auth/switch-org → backend revoca sesión actual + emite
 *      nuevo JWT con active_org_id.
 *   2. Frontend reemplaza el access_token en memoria.
 *   3. Invalida /me cache para que el SPA rehidrate todo el contexto.
 *   4. Hard reload para limpiar todos los TanStack queries cacheados
 *      con el client_id viejo (catálogos, facturas, etc).
 */

import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { authApi, setAccessToken } from '@/lib/api'
import { AUTH_ME_QUERY_KEY, useAuthContext } from '@/hooks/use-auth-context'

export function OrgSwitcher() {
  const auth = useAuthContext()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  // Cerrar al click fuera.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  // Solo mostramos el switcher si hay 2+ memberships activos.
  if (!auth.me || auth.memberships.length < 2) {
    return null
  }

  const activeOrg = auth.me.active_org

  async function handleSwitch(orgId: string) {
    if (orgId === activeOrg?.id) {
      setOpen(false)
      return
    }
    setSwitching(orgId)
    try {
      const resp = await authApi.switchOrg({ organization_id: orgId })
      setAccessToken(resp.access_token)
      toast.success(`Cambiado a ${resp.razon_social ?? 'otra empresa'}`)
      // Invalidar /me y todos los queries que dependen del client_id.
      qc.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
      qc.clear() // limpia TODO el cache — facturas, catálogos, etc.
      // Hard reload para evitar mezclar datos de orgs.
      window.location.href = '/dashboard'
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: { message?: string } } } }
      toast.error(err.response?.data?.detail?.message ?? (e as Error).message)
    } finally {
      setSwitching(null)
      setOpen(false)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-md border border-border bg-bg-app px-3 py-2 text-sm hover:bg-bg-elevated transition-colors"
      >
        <Building2 className="h-4 w-4 flex-none text-fg-muted" />
        <span className="flex-1 truncate text-left font-medium">
          {activeOrg?.razon_social ?? 'Sin empresa activa'}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 flex-none text-fg-muted transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-bg-surface shadow-lg">
          <ul className="py-1">
            {auth.memberships.map((m) => {
              const isActive = m.is_active_session_org
              const isSwitching = switching === m.organization_id
              return (
                <li key={m.membership_id}>
                  <button
                    type="button"
                    onClick={() => handleSwitch(m.organization_id)}
                    disabled={isSwitching || switching !== null}
                    className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-bg-elevated disabled:cursor-wait ${
                      isActive ? 'bg-brand-50/30' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">
                        {m.organization?.razon_social ?? '—'}
                      </p>
                      <p className="mt-0.5 text-xs text-fg-muted">
                        NIT {m.organization?.tax_id ?? '—'} · {m.role.toLowerCase()}
                      </p>
                    </div>
                    {isSwitching ? (
                      <Loader2 className="h-4 w-4 flex-none animate-spin text-brand-600" />
                    ) : isActive ? (
                      <Check className="h-4 w-4 flex-none text-brand-600" />
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
